import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import type { AgendaSlotDto } from "~/ui-rtk/api/agendaApi"
import { AGENDA_SLOT_PARAM } from "~/ui-rtk/components/features/agenda"
import { brandRoutes } from "~/ui-rtk/constants/routes"

/** Appends the agenda context (slot id + quantity) to a route. */
function withSlotContext(route: string, slot: AgendaSlotDto) {
  const params = new URLSearchParams({ [AGENDA_SLOT_PARAM]: slot.id })
  if (slot.quantity) params.set("count", String(slot.quantity))
  return `${route}?${params.toString()}`
}

/**
 * Resolves the "act on this slot" deep-link per slot type, carrying the slot
 * id + quantity so the target flow can show the agenda context and update the
 * slot's status in place. NOTE: links must target concrete tab routes — the
 * bare `/ad-blaster` root redirect drops query params.
 */
export function useAgendaActions() {
  const navigate = useNavigate()

  const runSlotAction = useCallback(
    (slot: AgendaSlotDto) => {
      switch (slot.type) {
        case "AD_BLAST":
          navigate(withSlotContext(brandRoutes.AD_BLASTER.CREATIVES, slot))
          break
        case "AD_CREATIVE":
          // The AdForge editor does not yet read a creativeId param, so link to
          // the creatives library where the draft lives.
          navigate(withSlotContext(brandRoutes.AD_BLASTER.CREATIVES, slot))
          break
        case "BUDGET_RECOMMENDATION":
          navigate(withSlotContext(brandRoutes.CAMPAIGN_MANAGEMENT.ROOT, slot))
          break
        case "GENERIC":
          if (!slot.targetUrl) break
          if (/^https?:\/\//i.test(slot.targetUrl)) {
            window.open(slot.targetUrl, "_blank", "noopener,noreferrer")
          } else {
            navigate(slot.targetUrl)
          }
          break
      }
    },
    [navigate],
  )

  return { runSlotAction }
}
